/**
 * React Bits — CountUp (TS + Tailwind), adapted from
 * https://reactbits.dev/text-animations/count-up
 *
 * Time-boxed ease-out (default 2s). A spring was letting the digits keep
 * ticking after the nominal duration; `animate()` stops on the clock.
 * Pass `format` to keep NAV/percent locale formatting.
 */

import { animate, useInView, useMotionValue } from 'motion/react';
import { useCallback, useEffect, useRef } from 'react';

interface CountUpProps {
  to: number;
  from?: number;
  direction?: 'up' | 'down';
  delay?: number;
  /** Seconds until the final value is shown. Default 2. */
  duration?: number;
  className?: string;
  startWhen?: boolean;
  format?: (value: number) => string;
  separator?: string;
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function CountUp({
  to,
  from = 0,
  direction = 'up',
  delay = 0,
  duration = 2,
  className = '',
  startWhen = true,
  format,
  separator = '',
}: CountUpProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const start = direction === 'down' ? to : from;
  const end = direction === 'down' ? from : to;
  const motionValue = useMotionValue(start);
  const isInView = useInView(ref, { once: true, margin: '0px' });

  const formatValue = useCallback(
    (latest: number) => {
      if (format) return format(latest);
      const options: Intl.NumberFormatOptions = {
        useGrouping: Boolean(separator),
        maximumFractionDigits: 4,
      };
      const formatted = Intl.NumberFormat('en-US', options).format(latest);
      return separator ? formatted.replace(/,/g, separator) : formatted;
    },
    [format, separator],
  );

  const write = useCallback(
    (latest: number) => {
      if (ref.current) ref.current.textContent = formatValue(latest);
    },
    [formatValue],
  );

  useEffect(() => {
    write(start);
  }, [start, write]);

  useEffect(() => {
    if (!isInView || !startWhen) return;
    if (prefersReducedMotion()) {
      write(end);
      return;
    }

    let controls: ReturnType<typeof animate> | undefined;
    const timeoutId = window.setTimeout(() => {
      controls = animate(motionValue, end, {
        duration,
        ease: [0.22, 1, 0.36, 1],
        onUpdate: write,
        onComplete: () => write(end),
      });
    }, delay * 1000);

    return () => {
      window.clearTimeout(timeoutId);
      controls?.stop();
    };
  }, [isInView, startWhen, motionValue, end, delay, duration, write]);

  return <span ref={ref} className={className} />;
}

export default CountUp;
